import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  Activity,
  BatteryCharging,
  Building2,
  ClipboardList,
  FileText,
  FolderCheck,
  LayoutDashboard,
  LucideAngularModule,
  ReceiptText,
  ScrollText,
  TriangleAlert,
  Users,
  Wallet,
} from 'lucide-angular';

type WorkflowStep = {
  title: string;
  summary: string;
  route: string;
  cta: string;
};

type ModuleCard = {
  title: string;
  summary: string;
  highlights: string[];
  route: string;
  routeLabel: string;
  icon: unknown;
};

type RoleGuide = {
  title: string;
  summary: string;
  checklist: string[];
};

@Component({
  standalone: true,
  selector: 'app-user-manual-prototype-page',
  imports: [CommonModule, RouterLink, LucideAngularModule],
  templateUrl: './user-manual-prototype.page.html',
  styleUrls: ['./user-manual-prototype.page.scss'],
})
export class UserManualPrototypePage {
  protected readonly FileTextIcon = FileText;
  protected readonly LayoutDashboardIcon = LayoutDashboard;
  protected readonly Building2Icon = Building2;
  protected readonly FolderCheckIcon = FolderCheck;
  protected readonly ScrollTextIcon = ScrollText;
  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly WalletIcon = Wallet;
  protected readonly ReceiptTextIcon = ReceiptText;
  protected readonly ActivityIcon = Activity;
  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly UsersIcon = Users;
  protected readonly TriangleAlertIcon = TriangleAlert;

  protected readonly workflow: WorkflowStep[] = [
    {
      title: '1. Register Business Parties',
      summary: 'Create vendor records first so downstream LOAs, contracts, and applications can link to valid parties.',
      route: '/vendors',
      cta: 'Open Vendors',
    },
    {
      title: '2. Prepare Required Documents',
      summary: 'Define mandatory vendor document rules before onboarding at scale to avoid missing compliance files later.',
      route: '/document-configurations',
      cta: 'Open Document Configuration',
    },
    {
      title: '3. Create LOAs and Contracts',
      summary: 'Build the operating and commercial backbone by creating LOAs, then linking them in contracts.',
      route: '/loas',
      cta: 'Open LOA Management',
    },
    {
      title: '4. Punch and Approve Applications',
      summary: 'Track current-month workload, monitor pending OA punch items, and send valid records for approval.',
      route: '/applications',
      cta: 'Open Applications',
    },
    {
      title: '5. Complete Billing and Reconciliation',
      summary: 'Use approved records in OA or SOP/TMR billing, then reconcile through reports, payment knock-off, and notes.',
      route: '/oa-billing',
      cta: 'Open OA Billing',
    },
  ];

  protected readonly modules: ModuleCard[] = [
    {
      title: 'Dashboard',
      summary: 'Start here to review pending activity, billing health, application counts, and power flow trends.',
      highlights: ['Quick actions', 'Power flow analysis', 'Billing snapshot'],
      route: '/',
      routeLabel: 'View Dashboard',
      icon: LayoutDashboard,
    },
    {
      title: 'Vendor Management',
      summary: 'Manage counterparty master data, contact details, types, and linked vendor users.',
      highlights: ['Create vendor', 'Search and filter', 'Open vendor profile'],
      route: '/vendors',
      routeLabel: 'Open Vendors',
      icon: Building2,
    },
    {
      title: 'Document Configuration',
      summary: 'Define mandatory vendor document rules by client type, effective date, and allowed format.',
      highlights: ['Mandatory files', 'Effective dates', 'File-type rules'],
      route: '/document-configurations',
      routeLabel: 'Open Document Configuration',
      icon: FolderCheck,
    },
    {
      title: 'LOA and Contracts',
      summary: 'Create approved LOAs first, then connect parties and LOAs inside commercial contracts.',
      highlights: ['Buy, sell, and banking LOAs', 'Contract linkage', 'Lifecycle tracking'],
      route: '/contracts',
      routeLabel: 'Open Contracts',
      icon: ScrollText,
    },
    {
      title: 'Applications and Approvals',
      summary: 'Monitor pending OA punch work, create applications, and route them through approval review.',
      highlights: ['Pending OA punch', 'Monthly advance view', 'Approval review'],
      route: '/application-approvals',
      routeLabel: 'Open Application Approval',
      icon: ClipboardList,
    },
    {
      title: 'Billing and Adjustments',
      summary: 'Generate OA or SOP/TMR billing, reconcile payments, and issue credit or debit notes when needed.',
      highlights: ['OA billing', 'Payment knock-off', 'Credit and debit notes'],
      route: '/payment-knock-off',
      routeLabel: 'Open Payment Knock-Off',
      icon: Wallet,
    },
    {
      title: 'Auto Fetch and Reports',
      summary: 'Bring in operational and report data, then compare fetched records with internal commercial data.',
      highlights: ['MCP and REA fetch', 'Schedule and price data', 'Contract analysis'],
      route: '/auto-fetch/rea-reports',
      routeLabel: 'Open REA Reports',
      icon: Activity,
    },
    {
      title: 'BESS Workspace',
      summary: 'Run battery operations, bid support, scheduling, forecast, and profitability workflows in one section.',
      highlights: ['Fleet monitoring', 'Bid workflow', 'Profit and loss'],
      route: '/bess/fleet-operations-matrix',
      routeLabel: 'Open BESS Monitoring',
      icon: BatteryCharging,
    },
  ];

  protected readonly roleGuides: RoleGuide[] = [
    {
      title: 'Operations User',
      summary: 'Focus on setup and transaction movement from vendor onboarding through application punching.',
      checklist: ['Review dashboard', 'Check LOAs and contracts', 'Create or monitor applications'],
    },
    {
      title: 'Billing User',
      summary: 'Focus on approved operational data and convert it into commercial outputs and reconciliations.',
      checklist: ['Validate approvals', 'Run billing', 'Perform payment and note adjustments'],
    },
    {
      title: 'Administrator',
      summary: 'Focus on access, menu visibility, staff setup, and support for blocked workflows.',
      checklist: ['Create users', 'Verify role access', 'Resolve setup gaps'],
    },
    {
      title: 'Vendor User',
      summary: 'Focus on records relevant to the linked vendor only, including profile, LOAs, contracts, and applications.',
      checklist: ['Review profile', 'Track applications', 'Monitor contract status'],
    },
  ];

  protected readonly dailyChecklist = [
    'Check dashboard alerts and pending counts.',
    'Review application and approval queues.',
    'Validate billing exceptions and overdue items.',
    'Fetch scheduled report data where required.',
    'Escalate blocked records with module name and record ID.',
  ];
}
